import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, CircularProgress, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getRankingHistory } from '../../services/ranking.service';

const RankingHistoryModal = ({ open, handleClose, jugId, jugNombre }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    if (open && jugId) {
      setLoading(true);
      getRankingHistory(jugId)
        .then((data) => {
          // Process data for chart
          const formattedData = data.map((item) => ({
            fecha: new Date(item.ranking.rkg_fecha).toLocaleDateString(),
            rawDate: item.ranking.rkg_fecha,
            puntaje: item.his_puntaje,
          })).reverse(); // Reverse to show chronological order
          setHistory(formattedData);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [open, jugId]);

  const filteredHistory = history.filter((item) => {
    if (timeRange === 'all') return true;
    const limitDate = new Date();
    limitDate.setMonth(limitDate.getMonth() - parseInt(timeRange, 10));
    return new Date(item.rawDate) >= limitDate;
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: '#0f172a',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: '#cbd5e1',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5, fontWeight: 600 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#cbd5e1' }}>
            Historial de Ranking - {jugNombre}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(e, next) => next !== null && setTimeRange(next)}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 0,
                '& .MuiToggleButton-root': {
                  color: '#94a3b8',
                  border: 'none',
                  borderRadius: 0,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    backgroundColor: '#268cf9',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#297dd6',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                },
              }}
            >
              <ToggleButton value="6">6 Meses</ToggleButton>
              <ToggleButton value="12">1 Año</ToggleButton>
              <ToggleButton value="all">Todo</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={handleClose} sx={{ color: '#94a3b8', '&:hover': { color: '#f8fafc' } }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredHistory.length > 0 ? (
          <Box height={400} mt={2} bgcolor="#1e293b" p={2} border="1px solid rgba(255, 255, 255, 0.08)" borderRadius={0}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={filteredHistory}>
                <CartesianGrid stroke="rgba(255, 255, 255, 0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="fecha" tick={{ fontSize: 12, fill: '#94a3b8' }} stroke="rgba(255, 255, 255, 0.1)" />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12, fill: '#94a3b8' }} stroke="rgba(255, 255, 255, 0.1)" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: 'rgba(255, 255, 255, 0.15)', color: '#cbd5e1' }} />
                <Line type="monotone" dataKey="puntaje" stroke="#297dd6ff" strokeWidth={2.5} dot={{ r: 4, stroke: '#268cf9', strokeWidth: 2, fill: '#1e293b' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box p={4} textAlign="center">
            <Typography sx={{ color: '#94a3b8' }}>No hay historial disponible para este periodo.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RankingHistoryModal;
